import { Capacitor, registerPlugin } from '@capacitor/core';
import { App as NativeApp } from '@capacitor/app';
import { OfflineStore, cachedPaths, target } from './offlineStore';

export const nativeOffline = Capacitor.getPlatform() === 'android';
if (nativeOffline) document.documentElement.classList.add('bp-native-android');
const Vault = registerPlugin('OfflineVault');
const change = () => window.dispatchEvent(new Event('bp-sync-state'));
let send;
let nativeConnected = navigator.onLine;
export const connectionOnline = () => nativeOffline ? nativeConnected : navigator.onLine;
export const offline = new OfflineStore({
  storage: { get: async () => (await Vault.read()).state || null, set: async (state) => { await Vault.write({ state: { ...state, session: state.backgroundAllowed ? state.session : null }, backend: process.env.REACT_APP_BACKEND_URL }); }, remove: async () => { await Vault.clear(); } },
  send: (...args) => send(...args), changed: change, online: connectionOnline,
});
export function installOffline(api) {
  if (!nativeOffline) return;
  Vault.networkStatus().then(({ connected }) => { nativeConnected = connected; change(); }).catch(change);
  Vault.addListener('networkStatusChange', ({ connected }) => { nativeConnected = connected; change(); if (connected) offline.sync().catch(change); });
  const adapter = api.defaults.adapter;
  // Resolve the Axios adapter without replacing interceptors or auth headers.
  send = async (method, url, data) => (await api.request({ method, url, data, offlineBypass: true })).data;
  api.defaults.adapter = async (config) => {
    const transport = (await import('axios')).default.getAdapter(adapter);
    if (config.offlineBypass) return transport(config);
    const path = config.url; const method = (config.method || 'get').toUpperCase();
    let data;
    if (method === 'GET' && cachedPaths.includes(path)) data = await offline.read(path);
    else if (['POST', 'PUT', 'DELETE'].includes(method) && target(path)) {
      data = await offline.enqueue(method, path, typeof config.data === 'string' ? JSON.parse(config.data || '{}') : config.data || {});
      // Return the local result before refresh listeners run, so form state and
      // the local projection cannot insert the same provisional record twice.
      setTimeout(() => offline.sync().catch(change), 50);
    } else {
      if (!connectionOnline()) throw new Error('This feature needs the internet. Reconnect and retry; nothing was submitted.');
      return transport(config);
    }
    return { data, status: 200, statusText: 'Local; sync pending', headers: {}, config };
  };
  window.addEventListener('online', () => { change(); offline.sync().catch(change); });
  window.addEventListener('offline', change);
  NativeApp.addListener('appStateChange', async ({ isActive }) => {
    if (isActive) {
      try { await offline.running; await offline.serial; if (offline.state.backgroundAllowed) await offline.restore(offline.state.session); await offline.sync(); } catch { change(); }
    }
  });
  setInterval(() => { if (offline.state.queue.length) offline.sync().catch(change); }, 15000);
}

if (nativeOffline) {
  NativeApp.addListener('backButton', ({ canGoBack }) => {
    if (document.querySelector('[role="dialog"]')) document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
    else if (canGoBack) window.history.back();
    else NativeApp.minimizeApp();
  });
  const viewport = () => document.documentElement.style.setProperty('--bp-viewport', `${window.visualViewport?.height || window.innerHeight}px`);
  window.visualViewport?.addEventListener('resize', viewport); viewport();
}
